import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import type { NotificationDeliveryDetail } from 'src/app/modules/administracion/services/notificaciones.service';

@Injectable({
  providedIn: 'root',
})
export class AdminGenericService {
  private urlAdmin = environment.API_SERVICES_ADMIN;
  private responseReporteSubject = new BehaviorSubject<any | null>(null);
  private typeUserSubject = new BehaviorSubject<any | null>(null);
  private sigService: string = this.urlAdmin + '/api/protected/sig-services';
  private sigServiceFree: string = this.urlAdmin + '/api/sig-services';

  typeUser$: Observable<any | null> = this.typeUserSubject.asObservable();

  constructor(private http: HttpClient) { }

  setResponseReporte(response: any) {
    this.responseReporteSubject.next(response);
  }

  getResponseReporte$(): Observable<any | null> {
    return this.responseReporteSubject.asObservable();
  }

  clear() {
    this.responseReporteSubject.next(null);
  }

  setTypeUser(response: any) {
    this.typeUserSubject.next(response);
  }

  getTypeUser(): any | null {
    return this.typeUserSubject.value;
  }

  clearTypeUser() {
    this.typeUserSubject.next(null);
  }

  obtenerContenido(contenType: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/content?contentType=${contenType}`
    );
  }

  obtenerContenidoLibre(contenType: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/content?contentType=${contenType}`
    );
  }

  guardarContenido(request: any) {
    return this.http.post<any>(
      `${this.urlAdmin}/api/protected/content`,
      request
    );
  }

  obtenerMantenimiento(
    maintenanceType: number,
    page: number,
    pageSize: number
  ) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/maintenance/maintenance-list?pageNumber=${page}&pageSize=${pageSize}&maintenanceType=${maintenanceType}`
    );
  }

  guardarCifras(request: any) {
    return this.http.put<any>(
      `${this.urlAdmin}/api/maintenance/protected/maintenance/numbers`,
      request
    );
  }

  obtenerMantenimientoPorId(id: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/protected/maintenance/${id}`
    );
  }

  crearMantenimiento(request: any) {
    return this.http.post<any>(
      `${this.urlAdmin}/api/maintenance/protected/maintenance`,
      request
    );
  }

  actualizarMantenimiento(request: any, id: number) {
    return this.http.put<any>(
      `${this.urlAdmin}/api/maintenance/protected/maintenance/${id}`,
      request
    );
  }

  eliminarMantenimineto(id: number) {
    return this.http.delete<any>(
      `${this.urlAdmin}/api/maintenance/protected/${id}`
    );
  }

  obtenerTotalVisitas() {
    return this.http.get<any>(`${this.urlAdmin}/api/event/visits-total`);
  }

  obtenerOtrasNotificaciones(query: string) {
    return this.http.get<any[]>(
      `${this.urlAdmin}/api/protected/other-notification?query=${query}`
    );
  }

  obtenerNotificacion(id: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/other-notification/${id}`
    );
  }

  obtenerDetalleEnvioNotificacion(id: number, pageNumber = 0, pageSize = 20) {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    return this.http.get<NotificationDeliveryDetail>(
      `${this.urlAdmin}/api/protected/other-notification/${id}/delivery-detail`,
      { params }
    );
  }

  eliminarNotificacion(id: number) {
    return this.http.delete<any>(
      `${this.urlAdmin}/api/protected/other-notification/${id}`
    );
  }

  guardarNotificacion(request: any) {
    return this.http.post<any>(
      `${this.urlAdmin}/api/protected/other-notification`,
      request
    );
  }

  editarNotificacion(request: any) {
    return this.http.put<any>(
      `${this.urlAdmin}/api/protected/other-notification`,
      request
    );
  }

  clonarNotificacion(id: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/other-notification/clone/${id}`
    );
  }

  restablecerPassword(email: string) {
    return this.http.post<void>(
      `${this.urlAdmin}/api/alert-mail/refresh-password-mail?mail=${encodeURIComponent(email)}`,
      null
    );
  }

  validarToken(token: string) {
    return this.http.post<any>(
      `${this.urlAdmin}/api/alert-mail/valid-mail-token?mailToken=${token}`,
      null
    );
  }

  obtenerServiciosInterOperable() {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/interoperability-services`
    );
  }

  obtenerInteroperPorId(id: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/interoperability-services/${id}`
    );
  }

  actualizarInteroper(request: any, id: number) {
    return this.http.put<any>(
      `${this.urlAdmin}/api/protected/interoperability-services?id=${id}`,
      request
    );
  }

  crearInteroper(request: any) {
    return this.http.post<any>(
      `${this.urlAdmin}/api/protected/interoperability-services`,
      request
    );
  }

  eliminarInteroperabilidad(id: number) {
    return this.http.delete<any>(
      `${this.urlAdmin}/api/protected/interoperability-services/${id}`
    );
  }

  obtenerServiciosInterOperablePorParametro(type: any, descripcion: any) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/protected/interoperability-services?description=${descripcion}&type=${type}`
    );
  }

  obtenerServicioSigByTipo(tipo: any) {
    return this.http.get<any>(`${this.sigService}?type=${tipo}`);
  }
  obtenerServicioSigFreeByTipo(tipo: any) {
    return this.http.get<any>(`${this.sigServiceFree}?type=${tipo}`);
  }

  crearServicioSig(request: any) {
    return this.http.post<any>(`${this.sigService}`, request);
  }

  obtenerServicioSigById(id: number) {
    return this.http.get<any>(`${this.urlAdmin}/api/sig-services/${id}`);
  }

  actualizarServicioSig(request: any, id: number) {
    return this.http.put<any>(`${this.sigService}?id=${id}`, request);
  }

  eliminarServicioSig(id: number) {
    return this.http.delete<any>(`${this.sigService}/${id}`);
  }

  obtenerEquipoByQuery(query: string, page: number, pageSize: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/maintenance/team-search-list?pageNumber=${page}&pageSize=${pageSize}&query=${query}`
    );
  }

  busquedaMantenimientoByQuery(
    query: string,
    page: number,
    pageSize: number,
    type: number
  ) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/maintenance/questions-search-list?pageNumber=${page}&pageSize=${pageSize}&type=${type}&query=${query}`
    );
  }

  busquedaMantenimientoByQueryType(
    module_: number,
    page: number,
    pageSize: number,
    maintenanceType: number,
    name: string
  ) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/maintenance/maintenance-search-list?pageNumber=${page}&pageSize=${pageSize}&maintenanceType=${maintenanceType}&module=${module_}&name=${name}`
    );
  }

  busquedaApiSearchList(
    page: number,
    pageSize: number,
    apiType?: number,
    query?: string
  ) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/maintenance/maintenance/api-search-list?pageNumber=${page}&pageSize=${pageSize}&apiType=${apiType}&query=${query}`
    );
  }

  actualizarContrasenia(request: any) {
    return this.http.put<any>(
      `${this.urlAdmin}/api/person`, request
    );
  }

  obtenerAniosPorEvento(type: number) {
    return this.http.get<any>(`${this.urlAdmin}/api/event/years-by-event?eventType=${type}`);
  }

  obtenerAnios(type: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/interoperability-services/years?type=${type}`
    );
  }

  obtenerServiciosInterAnioTipo(type?: number, anio?: number) {
    return this.http.get<any>(
      `${this.urlAdmin}/api/interoperability-services?year=${anio}&type=${type}`
    );
  }

  exportarVisitasPorAnio(year: number) {
    return this.descargarExcel('/api/protected/event/export/visits/by-year', { year });
  }

  exportarUsuariosPorAnio(year: number) {
    return this.descargarExcel('/api/protected/event/export/users/by-year', { year });
  }

  exportarUsuariosPorInstitucion(year: number) {
    return this.descargarExcel('/api/protected/event/export/users/by-institution', { year });
  }

  exportarDescargasPorAnio(year: number) {
    return this.descargarExcel('/api/protected/event/export/downloads/by-year', { year });
  }

  exportarDescargasPorPeriodo(initialYear: number, finishYear: number) {
    return this.descargarExcel('/api/protected/event/export/downloads/by-period', {
      initialYear,
      finishYear,
    });
  }

  private descargarExcel(path: string, params: Record<string, number>) {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      httpParams = httpParams.set(key, String(value));
    });

    return this.http.get(`${this.urlAdmin}${path}`, {
      params: httpParams,
      observe: 'response',
      responseType: 'blob',
    });
  }
}
